/**
 * PTEL Tool - Step2 DS v2 Color + Config Adapter
 * Mapeo tokens Step2 legacy → Design System v2
 *
 * Proporciona:
 *   - dsColors: reemplaza tokens[theme] (colores planos modo oscuro)
 *   - getTypologyConfig(): reemplaza legacy getTypologyConfig() de tokens.ts
 *   - getSourceConfigLegacy(): reemplaza legacy getSourceConfig() de tokens.ts
 *   - getProgressColor / getScoreColor: re-exports de DS v2
 *
 * @version 3.0.0
 * @task F8.15 — migración completa Step2 raíz → DS v2
 */

import {
  colors,
  getSourceConfig as dsGetSourceConfig,
  getProgressColor,
  getScoreColor,
  getTypologyName,
  type TypologyCode,
} from '@/design-system/v2/design-tokens';

import type { ColorTokens } from './types';

// Re-export helpers directos de DS v2 para que Step2.tsx no necesite doble import
export { getProgressColor, getScoreColor };

// ─── Mapping: legacy TypologyKey → DS v2 TypologyCode ──────────────────────

const TYPO_KEY_TO_CODE: Record<string, TypologyCode> = {
  SANITARIO: 'san', EDUCATIVO: 'edu', POLICIAL: 'pol',
  CULTURAL: 'cul', RELIGIOSO: 'rel', EMERGENCIAS: 'eme',
  DEPORTIVO: 'dep', ADMINISTRATIVO: 'adm', VIAL: 'via',
  ENERGETICO: 'ene', HIDRAULICO: 'hid', OTRO: 'otr',
};

const TYPO_CODE_LABEL: Record<TypologyCode, string> = {
  san: 'SAN', edu: 'EDU', pol: 'POL', cul: 'CUL', rel: 'REL',
  eme: 'EME', dep: 'DEP', adm: 'ADM', via: 'VIA', ene: 'ENE',
  hid: 'HID', otr: 'OTR', seg: 'POL', inf: 'INF',
};

export interface LegacyTypologyConfig {
  code: string;   // 'SAN', 'EDU', …
  label: string;   // 'Sanitario', …
  color: string;   // hex del tema oscuro
  bgAlpha: string; // rgba(…, 0.15)
}

/** Drop-in replacement para legacy getTypologyConfig(item.tipo) */
export function getTypologyConfig(typoKey: string): LegacyTypologyConfig {
  const dsCode = TYPO_KEY_TO_CODE[typoKey] ?? 'otr';
  const typo = colors.typologies[dsCode] ?? colors.typologies.otr;
  return {
    code: TYPO_CODE_LABEL[dsCode] ?? 'OTR',
    label: getTypologyName(dsCode),
    color: typo.color,
    bgAlpha: typo.bgAlpha,
  };
}

/** Drop-in replacement para legacy getSourceConfig(item.source) */
export function getSourceConfigLegacy(source: string): { label: string; color: string; bgAlpha: string } {
  const cfg = dsGetSourceConfig(source);
  return { label: cfg.label, color: cfg.color, bgAlpha: cfg.bgAlpha };
}

// ─── Color adapter: tokens[theme] → DS v2 flat ─────────────────────────────

/**
 * Adaptador plano: nombres legacy Step2 → tokens DS v2.
 * Solo modo oscuro (el único activo en producción).
 * Satisface la interfaz ColorTokens completa.
 */
export const dsColors: ColorTokens = {
  // Fondos
  bg: colors.bg.primary,
  bgAlt: colors.bg.secondary,
  bgElevated: colors.bg.hover,
  bgMuted: '#1F1F1E',

  // Bordes
  border: colors.border.DEFAULT,

  // Texto
  text: colors.fg.primary,
  textSecondary: colors.fg.secondary,
  textMuted: colors.fg.muted,
  textDisabled: colors.fg.subtle,

  // Acento
  accent: colors.primary,
  accentHover: colors.primaryHover,

  // Semánticos
  success: colors.success,
  successBg: colors.successMuted,
  warning: colors.warning,
  warningBg: colors.warningMuted,
  error: colors.error,
  errorBg: colors.errorMuted,
  info: colors.scores.medium,
  infoBg: colors.scoresBg.medium,

  // Score colors (tokens.css alignment)
  scoreExcellent: colors.scores.high,
  scoreExcellentBg: colors.scoresBg.high,
  scoreGood: colors.scores.medium,
  scoreGoodBg: colors.scoresBg.medium,
  scoreMedium: colors.scores.low,
  scoreMediumBg: colors.scoresBg.low,
  scoreLow: colors.scores.critical,
  scoreLowBg: colors.scoresBg.critical,

  // Iconos
  iconMuted: colors.fg.muted,
  iconActive: colors.success,
} as const;
